// components/socials/profile/ProfilePostsSection.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiGrid, FiList, FiPlus } from "react-icons/fi";

import { db } from "../../../firebase/firebase.utils";

const PAGE_SIZE = 30;
const PASTELS = [
  "#f3d9e7",
  "#dde7ff",
  "#d8f3e6",
  "#ffe7d6",
  "#e8d9ff",
  "#dff2ff",
  "#ffe1ea",
  "#e6f0d6",
];

const hash = (value = "") => {
  let result = 0;
  for (let index = 0; index < value.length; index += 1) {
    result = (result * 31 + value.charCodeAt(index)) >>> 0;
  }
  return result;
};

const coverFor = (post) => {
  if (post?.coverMediaUrl) return post.coverMediaUrl;
  const first = Array.isArray(post?.media) ? post.media[0] : null;
  if (!first) return null;
  if (first.coverUrl) return first.coverUrl;
  return (first.kind || first.type) === "image"
    ? first.url || first.mediaUrl
    : null;
};

export default function ProfilePostsSection({
  profileUserId,
  isOwnProfile,
  isConnected = false,
  onOpenPost,
}) {
  const navigate = useNavigate();
  const userChangedMode = useRef(false);
  const loadPageRef = useRef(null);
  const moreRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [postsError, setPostsError] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [viewMode, setViewMode] = useState("grid");

  const openPost = useCallback(
    (postId) => {
      if (!postId) return;
      if (onOpenPost) {
        onOpenPost(postId);
        return;
      }
      navigate(
        `/profile/${encodeURIComponent(profileUserId)}?post=${encodeURIComponent(postId)}`,
      );
    },
    [navigate, onOpenPost, profileUserId],
  );

  useEffect(() => {
    userChangedMode.current = false;
    setViewMode("grid");
  }, [profileUserId]);

  useEffect(() => {
    let active = true;
    let cursor = null;
    let more = !!profileUserId;
    let busy = false;
    let failed = false;
    let firstPage = true;
    setPosts([]);
    setPostsError(false);
    setHasMore(more);
    setLoading(more);
    setLoadingMore(false);

    const loadPage = async (retry = false) => {
      if (!active || busy || !more || (failed && !retry)) return;
      busy = true;
      failed = false;
      setPostsError(false);
      firstPage ? setLoading(true) : setLoadingMore(true);

      try {
        let nextPosts = [];
        // Advance past deleted-only pages using the raw snapshot cursor.
        do {
          let postsQuery = db
            .collection("posts")
            .where("authorId", "==", profileUserId);
          if (!isOwnProfile) {
            postsQuery = isConnected
              ? postsQuery.where("visibility", "in", ["public", "connections"])
              : postsQuery.where("visibility", "==", "public");
          }
          postsQuery = postsQuery.orderBy("createdAt", "desc");
          if (cursor) postsQuery = postsQuery.startAfter(cursor);
          const snapshot = await postsQuery.limit(PAGE_SIZE).get();
          if (!active) return;
          cursor = snapshot.docs[snapshot.docs.length - 1] || cursor;
          more = snapshot.size === PAGE_SIZE;
          nextPosts = snapshot.docs
            .map((document) => ({ ...document.data(), id: document.id }))
            .filter((post) => !post.isDeleted);
        } while (more && !nextPosts.length);

        setPosts((previous) => {
          const ids = new Set(previous.map((post) => post.id));
          return [
            ...previous,
            ...nextPosts.filter((post) => !ids.has(post.id)),
          ];
        });
        setHasMore(more);
        firstPage = false;
      } catch (error) {
        if (active) {
          console.log("Profile posts load error:", error);
          failed = true;
          setPostsError(true);
        }
      } finally {
        busy = false;
        if (active) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };

    loadPageRef.current = loadPage;
    loadPage();
    return () => {
      active = false;
      loadPageRef.current = null;
    };
  }, [profileUserId, isOwnProfile, isConnected]);

  useEffect(() => {
    let active = true;
    setFeaturedPosts([]);
    setFeaturedLoading(!!profileUserId);

    const load = async () => {
      if (!profileUserId) return;
      try {
        const userSnapshot = await db
          .collection("users")
          .doc(profileUserId)
          .get();
        if (!active) return;
        const user = userSnapshot.exists ? userSnapshot.data() : {};
        const preferredMode =
          user.profilePostsDisplay === "list" ? "list" : "grid";
        const featuredIds = Array.isArray(user.featuredPostIds)
          ? [
              ...new Set(
                user.featuredPostIds.filter(
                  (id) => typeof id === "string" && id && !id.includes("/"),
                ),
              ),
            ].slice(0, 20)
          : [];

        if (!userChangedMode.current) setViewMode(preferredMode);
        // Individual reads work on the legacy SDK and isolate denied/missing posts.
        const found = await Promise.all(
          featuredIds.map(async (id) => {
            try {
              const snapshot = await db.collection("posts").doc(id).get();
              return snapshot.exists
                ? { ...snapshot.data(), id: snapshot.id }
                : null;
            } catch (error) {
              if (error.code !== "permission-denied")
                console.log("Featured post load error:", error);
              return null;
            }
          }),
        );

        if (active)
          setFeaturedPosts(
            found.filter(
              (post) =>
                post &&
                !post.isDeleted &&
                post.authorId === profileUserId &&
                (isOwnProfile ||
                  post.visibility === "public" ||
                  (isConnected && post.visibility === "connections")),
            ),
          );
      } catch (error) {
        if (active) console.log("Featured posts load error:", error);
      } finally {
        if (active) setFeaturedLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [profileUserId, isOwnProfile, isConnected]);

  useEffect(() => {
    if (
      loading ||
      loadingMore ||
      !hasMore ||
      postsError ||
      !moreRef.current ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadPageRef.current?.();
      },
      {
        root: moreRef.current.closest(".profile-page__posts-scroll"),
        rootMargin: "300px",
      },
    );
    observer.observe(moreRef.current);
    return () => observer.disconnect();
  }, [
    loading,
    loadingMore,
    hasMore,
    postsError,
    posts.length,
    viewMode,
    featuredLoading,
  ]);

  const featuredIds = useMemo(
    () => new Set(featuredPosts.map((post) => post.id)),
    [featuredPosts],
  );

  if (!profileUserId) return null;

  return (
    <section className="profile-posts">
      <header className="profile-posts__header">
        <h2>Posts</h2>
        <div>
          <button
            type="button"
            className="icon-button"
            onClick={() => {
              userChangedMode.current = true;
              setViewMode((mode) => (mode === "grid" ? "list" : "grid"));
            }}
            aria-label={viewMode === "grid" ? "Use list view" : "Use grid view"}
          >
            {viewMode === "grid" ? <FiList /> : <FiGrid />}
          </button>
          {isOwnProfile && (
            <button type="button" onClick={() => navigate("/post/new")}>
              <FiPlus /> New
            </button>
          )}
        </div>
      </header>

      {featuredLoading ? (
        <div className="profile-posts__status">Loading featured posts…</div>
      ) : featuredPosts.length ? (
        <div className="profile-posts__featured-wrap">
          <strong>Featured</strong>
          <div className="profile-posts__featured">
            {featuredPosts.map((post) => (
              <button
                type="button"
                key={post.id}
                onClick={() => openPost(post.id)}
              >
                <PostCover post={post} />
                <span>{post.text || "Post"}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="profile-posts__status">Loading posts…</div>
      ) : !posts.length ? (
        !postsError && <div className="profile-posts__empty">No posts yet.</div>
      ) : viewMode === "grid" ? (
        <div className="profile-posts__grid">
          {posts.map((post) => (
            <button
              type="button"
              key={post.id}
              onClick={() => openPost(post.id)}
            >
              <PostCover post={post} />
              {featuredIds.has(post.id) && (
                <span className="featured-mark">Featured</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="profile-posts__list">
          {posts.map((post) => (
            <button
              type="button"
              key={post.id}
              onClick={() => openPost(post.id)}
            >
              <PostCover post={post} />
              <span>
                <strong>{post.text || "(No text)"}</strong>
                {!!post.tags?.length && (
                  <small>
                    {post.tags
                      .slice(0, 6)
                      .map((tag) => `#${tag}`)
                      .join("  ")}
                  </small>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
      {!loading && hasMore && (
        <div ref={moreRef} className="profile-posts__status" aria-live="polite">
          {postsError && <span>Could not load posts. </span>}
          {loadingMore ? (
            "Loading posts…"
          ) : (
            <button type="button" onClick={() => loadPageRef.current?.(true)}>
              {postsError ? "Try again" : "Load more"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function PostCover({ post }) {
  const coverUrl = coverFor(post);
  const mediaCount = Array.isArray(post.media) ? post.media.length : 0;
  return (
    <span
      className="profile-posts__cover"
      style={{ backgroundColor: PASTELS[hash(post.id) % PASTELS.length] }}
    >
      {coverUrl ? (
        <img src={coverUrl} alt="" loading="lazy" />
      ) : (
        <span>{post.text?.trim() || "Post"}</span>
      )}
      {mediaCount > 1 && <em>{mediaCount}</em>}
    </span>
  );
}
